import { inject, Injectable } from '@angular/core';

import { TranslocoService } from '@ngneat/transloco';

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const XLSX_MAX_COLUMN_WIDTH = 50;
const PDF_LANDSCAPE_COLUMN_THRESHOLD = 6;
const PDF_LANDSCAPE_CONTENT_LENGTH = 120;
const PDF_MIN_CELL_WIDTH = 24;

export type SvcExportFormat = 'xlsx' | 'pdf';

export interface SvcExportColumn {
  header: string;
  key: string;
}

export type SvcExportRow = Readonly<Record<string, string | number>>;

export interface SvcExportRequest {
  fileName: string;
  sheetTitle: string;
  columns: readonly SvcExportColumn[];
  rows: readonly SvcExportRow[];
}

function downloadBlob(blob: Blob, fileName: string): void {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
}

/**
 * Generic XLSX/PDF export for a plain columns+rows table, for consumers without a
 * `SvcDataTableComponent` (e.g. Alert's raw `z-table`). Promoted from Knowledge Center's
 * `KctExportFileService` (AB#72289); that file stays untouched (frozen).
 */
@Injectable({ providedIn: 'root' })
export class SvcExportFileService {
  private readonly _transloco = inject(TranslocoService);

  export(format: SvcExportFormat, request: SvcExportRequest): Promise<void> {
    return format === 'xlsx' ? this.exportXlsx(request) : this.exportPdf(request);
  }

  private async exportXlsx(request: SvcExportRequest): Promise<void> {
    const excelModule = await import('exceljs');
    const ExcelJS = excelModule.default ?? excelModule;
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet(this._transloco.translate(request.sheetTitle));

    sheet.columns = request.columns.map((column) => {
      const width = this.columnWidth(column, request.rows);
      return {
        header: this._transloco.translate(column.header),
        key: column.key,
        width,
        style: { alignment: { vertical: 'middle' as const, wrapText: width >= XLSX_MAX_COLUMN_WIDTH } },
      };
    });
    sheet.getRow(1).font = { bold: true };

    for (const row of request.rows) {
      sheet.addRow(row);
    }

    const buffer = await workbook.xlsx.writeBuffer();
    downloadBlob(new Blob([buffer], { type: XLSX_MIME_TYPE }), `${this._transloco.translate(request.fileName)}.xlsx`);
  }

  private async exportPdf(request: SvcExportRequest): Promise<void> {
    const [{ jsPDF }, autoTable] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
    const doc = new jsPDF(this.pdfOrientation(request));

    autoTable.default(doc, {
      head: [request.columns.map((column) => this._transloco.translate(column.header))],
      body: request.rows.map((row) => request.columns.map((column) => String(row[column.key] ?? ''))),
      styles: { fontSize: 9, valign: 'middle', halign: 'left', overflow: 'linebreak', cellWidth: 'auto' },
      columnStyles: Object.fromEntries(Array.from(request.columns.keys(), (index) => [index, { cellWidth: 'auto', minCellWidth: PDF_MIN_CELL_WIDTH, overflow: 'linebreak' }])),
    });

    doc.save(`${this._transloco.translate(request.fileName)}.pdf`);
  }

  private pdfOrientation(request: SvcExportRequest): 'landscape' | 'portrait' {
    if (request.columns.length > PDF_LANDSCAPE_COLUMN_THRESHOLD) {
      return 'landscape';
    }
    const hasLongContent = request.rows.some((row) => request.columns.some((column) => String(row[column.key] ?? '').length > PDF_LANDSCAPE_CONTENT_LENGTH));
    return hasLongContent ? 'landscape' : 'portrait';
  }

  private columnWidth(column: SvcExportColumn, rows: readonly SvcExportRow[]): number {
    const headerLength = this._transloco.translate(column.header).length;
    const longestCell = rows.reduce((longest, row) => Math.max(longest, String(row[column.key] ?? '').length), 0);
    return Math.min(Math.max(headerLength, longestCell) + 2, XLSX_MAX_COLUMN_WIDTH);
  }
}
